import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbModal from '../security-toolbox-ui/StbModal';
import {
  useLabDeviceTypes,
  useLabExecutionMethods,
  useLabProducts,
  useSaveLabRow,
} from '../../hooks/lab-hooks';
import {
  LabDeviceType,
  labMethodSummary,
  labSlugify,
  LAB_TABLES,
  LAB_TRANSPORTS,
} from '../../support/labService';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';
import { LabSpecBuilder } from './LabSpecFields';

export type LabDeviceTypeEditorModalProps = {
  open: boolean;
  /** Null creates. An existing row keeps its key - items and devices refer to it. */
  deviceType: LabDeviceType | null;
  onClose: () => void;
  onSaved?: (deviceType: LabDeviceType) => void;
};

const EMPTY: Partial<LabDeviceType> = {
  key: '',
  name: '',
  description: '',
  product_uid: null,
  product_name: '',
  default_transport: 'ssh',
  default_port: null,
  methods: [],
  order: 0,
  enabled: true,
  metadata: {},
};

/**
 * Author one kind of device.
 *
 * Two things a type does. It stops an item written for a firewall being attached to a Windows host -
 * that check runs when somebody attaches, where they can act on it. And it says which methods can
 * reach a box of this kind, which is the other half of the same check.
 *
 * The method list left empty means unconstrained, not "nothing allowed". That is deliberate: an
 * under-specified list refuses legitimate work, and being refused for a reason nobody intended is
 * worse than not checking. Tick the ones you are sure of, or none.
 *
 * The Cisco product is stored by value - the id and a copy of the name - with no foreign key. These
 * rows are mirrored wholesale into the lab controller's app, which has no product table, so a key
 * would not survive the copy. The id is authoritative; the name is the readable half and is
 * refreshed whenever the product is re-picked here.
 */
const LabDeviceTypeEditorModal: React.FC<LabDeviceTypeEditorModalProps> = ({
  open,
  deviceType,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabDeviceType>>(EMPTY);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');

  const save = useSaveLabRow<Partial<LabDeviceType> & { id?: number }>(LAB_TABLES.deviceType);

  const { data: catalogue } = useLabDeviceTypes({}, { enabled: open });
  const { data: methodRows } = useLabExecutionMethods({}, { enabled: open });
  // Two thousand products, fetched only while this is open. It is the one select that needs them.
  const { data: productRows, isLoading: productsLoading } = useLabProducts({ enabled: open });

  const types = useMemo(() => catalogue || [], [catalogue]);
  const methods = useMemo(() => methodRows || [], [methodRows]);
  const products = useMemo(() => productRows || [], [productRows]);

  useEffect(() => {
    if (!open) return;

    setDraft(
      deviceType ? { ...deviceType, methods: [...(deviceType.methods || [])] } : { ...EMPTY },
    );
    setJsonError(null);
    setFormError(null);
    setProductSearch('');
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deviceType?.id]);

  const set = (patch: Partial<LabDeviceType>) => setDraft((current) => ({ ...current, ...patch }));

  const generatedKey = useMemo(
    () => (deviceType ? deviceType.key : labSlugify(draft.name || '')),
    [deviceType, draft.name],
  );

  /**
   * Products the search matches, capped.
   *
   * Capped rather than paged because this is a picker, not a browse: two thousand options in a
   * scrolling list is the same as none, and the answer to "I cannot find it" is a narrower search.
   */
  const productMatches = useMemo(() => {
    const needle = productSearch.trim().toLowerCase();

    if (!needle) return [];

    return products
      .filter((product) => (product.name || '').toLowerCase().includes(needle))
      .slice(0, 25);
  }, [products, productSearch]);

  const toggleMethod = (id: number) => {
    const current = draft.methods || [];

    set({
      methods: current.includes(id)
        ? current.filter((entry) => entry !== id)
        : [...current, id],
    });
  };

  const handleSubmit = async () => {
    if (!draft.name?.trim()) {
      setFormError('Give the device type a name.');
      return;
    }

    if (!generatedKey) {
      setFormError('No key could be derived from that name. Use letters or digits somewhere in it.');
      return;
    }

    const clash = types.find(
      (candidate) => candidate.id !== draft.id && candidate.key === generatedKey,
    );

    if (clash) {
      setFormError(
        `"${clash.name}" already uses the key ${generatedKey}. Edit that one instead, or name this differently.`,
      );
      return;
    }

    if (jsonError) {
      setFormError(`Metadata: ${jsonError}`);
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync({ ...draft, key: generatedKey });
      onSaved?.(saved as LabDeviceType);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);
  const itemCount = deviceType?.item_count ?? 0;
  const deviceCount = deviceType?.device_count ?? 0;

  return (
    <StbModal
      open={open}
      role="update"
      title={deviceType ? `Edit ${deviceType.name}` : 'New device type'}
      size="large"
      scrollable
      submitLabel={deviceType ? 'Save device type' : 'Create device type'}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {(itemCount > 0 || deviceCount > 0) && (
          <p className="m-0 text-sm text-gray-600">
            {itemCount} item{itemCount === 1 ? '' : 's'} written for this type, on {deviceCount} device
            {deviceCount === 1 ? '' : 's'}. Narrowing the method list below can make an attachment
            that already exists illegal - the compile reports those rather than refusing, so nothing
            already authored stops being compilable.
          </p>
        )}

        <TextField
          label="Name"
          value={draft.name || ''}
          onChange={(evt) => set({ name: evt.target.value })}
          helperText="What an author picks it out of a list by - Windows Server, Ubuntu, Cisco FTD."
          fullWidth
        />

        <LabDerivedKey
          value={generatedKey}
          frozen={!!deviceType}
          label="Type key"
          usage="Items and devices refer to the type by it."
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => set({ description: evt.target.value })}
          helperText="What is true of every box of this kind, and what an author should not assume of it."
          multiline
          minRows={2}
          fullWidth
        />

        {/* By value, not by key. See the component docstring - the mirror is why. */}
        <div className="rounded-md border border-gray-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-700">Cisco product</span>
            {draft.product_uid ? (
              <LabStatusTag label={`product ${draft.product_uid}`} sentiment="success" />
            ) : (
              <LabStatusTag label="Not a product" />
            )}
          </div>

          <p className="m-0 mt-1 text-xs text-gray-500">
            For the types that are a Cisco product. Windows, the Linux variants and vCenter leave this
            empty. The id is what is stored and what is authoritative; the name is a snapshot kept for
            reading, and re-picking here refreshes it.
          </p>

          {draft.product_uid ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">{draft.product_name || `product ${draft.product_uid}`}</span>
              <button
                type="button"
                className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-50"
                onClick={() => set({ product_uid: null, product_name: '' })}
              >
                Clear
              </button>
            </div>
          ) : (
            <>
              <TextField
                label="Search products"
                value={productSearch}
                onChange={(evt) => setProductSearch(evt.target.value)}
                helperText={
                  productsLoading
                    ? 'Loading the product catalogue...'
                    : 'Cisco products only. Type two or three letters of the name.'
                }
                fullWidth
                size="small"
                className="mt-2"
              />

              {!!productMatches.length && (
                <div className="mt-2 max-h-48 overflow-y-auto rounded border border-gray-100">
                  {productMatches.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className="block w-full border-b border-gray-100 px-2 py-1.5 text-left text-sm last:border-b-0 hover:bg-gray-50"
                      onClick={() => {
                        set({ product_uid: product.id, product_name: product.name });
                        setProductSearch('');
                      }}
                    >
                      {product.name}
                      <span className="ml-2 font-mono text-xs text-gray-500">{product.id}</span>
                    </button>
                  ))}
                </div>
              )}

              {!!productSearch.trim() && !productMatches.length && !productsLoading && (
                <p className="m-0 mt-2 text-xs text-gray-500">
                  Nothing matches. Only Cisco products are listed here.
                </p>
              )}
            </>
          )}
        </div>

        <LabFieldRow columns={3}>
          <LabOptionSelect
            label="Default transport"
            value={draft.default_transport}
            options={LAB_TRANSPORTS}
            onChange={(default_transport) => set({ default_transport })}
            helpText="What a device of this kind is reached by unless the device says otherwise."
          />
          <TextField
            label="Default port"
            type="number"
            value={draft.default_port ?? ''}
            onChange={(evt) =>
              set({ default_port: evt.target.value === '' ? null : Number(evt.target.value) })
            }
            helperText="Leave empty for the transport's own."
            fullWidth
          />
          <TextField
            label="Order"
            type="number"
            value={draft.order ?? 0}
            onChange={(evt) => set({ order: evt.target.value === '' ? 0 : Number(evt.target.value) })}
            helperText="Where it sits in the pick lists."
            fullWidth
          />
        </LabFieldRow>

        <LabCheckbox
          label="Enabled"
          checked={draft.enabled !== false}
          onChange={(enabled) => set({ enabled })}
          helpText="Disabling keeps every item and device that refers to it working, and takes it out of the pick lists for new ones."
        />

        <div className="rounded-md border border-gray-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-700">Methods this can be reached by</span>
            <LabStatusTag
              label={
                (draft.methods || []).length
                  ? `${(draft.methods || []).length} selected`
                  : 'Unconstrained'
              }
              sentiment={(draft.methods || []).length ? 'info' : 'warning'}
            />
          </div>

          <p className="m-0 mt-1 text-xs text-gray-500">
            An item using a method that is not on this list is refused when somebody tries to attach
            it to a device of this type. Leaving it empty means no such check runs, which is the right
            answer when you are not sure - an under-specified list refuses real work for a reason
            nobody intended.
          </p>

          {!methods.length ? (
            <p className="m-0 mt-2 text-xs text-gray-500">
              There are no execution methods yet, so there is nothing to constrain. Author them on the
              Methods tab.
            </p>
          ) : (
            <div className="mt-2 max-h-56 overflow-y-auto rounded border border-gray-100">
              {methods.map((method) => (
                <label
                  key={method.id}
                  className="flex items-start gap-2 border-b border-gray-100 px-2 py-1.5 text-sm last:border-b-0 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={(draft.methods || []).includes(method.id)}
                    onChange={() => toggleMethod(method.id)}
                  />
                  <span>
                    <span className="font-medium">{method.name}</span>
                    <span className="ml-2 font-mono text-xs text-gray-500">{method.key}</span>
                    <span className="block text-xs text-gray-500">{labMethodSummary(method)}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.deviceTypeMetadata}
          value={draft.metadata}
          resetKey={deviceType?.id ?? 'new'}
          onChange={(metadata, error) => {
            setJsonError(error);
            if (!error) set({ metadata });
          }}
        />
      </div>
    </StbModal>
  );
};

export default LabDeviceTypeEditorModal;
